import { useEffect, useState } from "react";
import { apiGetSupportCaseOptions } from "../api/supportCaseApi";
import {
  normalizeCaseOptions,
  FALLBACK_CASE_CATEGORIES,
} from "../utils/supportCaseVocabulary";

/**
 * useSupportCaseOptions: the live support case vocabulary.
 *
 * GET /api/support-cases/meta/options is the source of truth for the category,
 * status and priority lists, so no screen hardcodes an enum. This hook is the
 * one place that reads it, which is also why no screen has to think about the
 * failure case: utils/supportCaseVocabulary.js ships a fallback category list,
 * and the hook hands that back if the call never lands. A customer with no
 * signal can still pick a category and report an issue; the server validates
 * the value either way.
 *
 * Cached at module scope like useCodPolicy: the vocabulary changes about never,
 * and the report form, the list filter and the detail screen should not each
 * cost a request.
 *
 * `live` is false while the shipped fallback is standing in, so a caller can
 * tell the difference if it ever needs to.
 */

const SHIPPED = normalizeCaseOptions(null);

let cachedOptions = null;
let inFlight = null;

const loadOptions = () => {
  if (cachedOptions) return Promise.resolve(cachedOptions);
  if (!inFlight) {
    inFlight = apiGetSupportCaseOptions()
      .then((payload) => {
        const options = normalizeCaseOptions(payload);
        // Only cache a genuinely live answer, so a blip does not pin the
        // fallback in memory for the rest of the session.
        if (options.live) cachedOptions = options;
        return options;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export default function useSupportCaseOptions() {
  const [options, setOptions] = useState(cachedOptions || SHIPPED);
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    let alive = true;
    loadOptions().then((next) => {
      if (!alive) return;
      if (next) setOptions(next);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return {
    categories: options.categories || FALLBACK_CASE_CATEGORIES,
    statuses: options.statuses,
    priorities: options.priorities,
    live: options.live === true,
    loading,
  };
}
